import { useAppBuilderContext } from 'app/app/editor/context';
import { MouseEvent } from 'react';
import { alpha, Box, Stack, Theme } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import NodeCssElements from 'app/app/editor/tabs/elements/layout/NodeCssElements';
import { IComponentNode } from 'app/app/editor/index.functions';
import NodeNameLine from 'app/app/editor/tabs/elements/layout/NodeNameLine';
import { useElementIsActive } from './useElementIsActive';

const PANEL_HIGHLIGHT_PADDING = 24; // Offset for tree indentation and icon spacing
export default function LayoutNode({
    node,
    componentUid,
    handleOpenContextMenu,
    isOpen,
    toggleOpen,
    panelWidth,
}: {
    node: IComponentNode;
    componentUid: string | null;
    handleOpenContextMenu: (event: MouseEvent, nodeUid: string) => void;
    isOpen: boolean;
    toggleOpen: () => void;
    panelWidth: number | null;
}) {
    const ctx = useAppBuilderContext();
    const page = ctx.page;

    const isComponent = null !== node.params?.uid && page?.components?.[node.params?.uid];

    const elementIsActive = useElementIsActive(componentUid, node.params?.uid);

    return (
        <Box
            sx={{
                position: 'relative',
                whiteSpace: 'nowrap',
                padding: '7px 0',
                cursor: 'pointer',
                ...(elementIsActive
                    ? {
                          '&::before': {
                              content: '""',
                              backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
                              borderRadius: '3px',
                              position: 'absolute',
                              height: '30px',
                              right: '0',
                              top: '0',
                              zIndex: '-1',
                              width: panelWidth ? `${panelWidth - PANEL_HIGHLIGHT_PADDING}px` : '100%',
                          },
                      }
                    : {}),
            }}
        >
            <Stack direction={'row'} onContextMenu={(event) => handleOpenContextMenu(event, node.params?.uid)}>
                <Box
                    sx={{
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: elementIsActive ? 'primary.main' : 'text.primary',
                    }}
                >
                    {!isComponent ? (
                        <Box
                            sx={{
                                width: '14px',
                                height: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                '&:hover': {
                                    cursor: 'pointer',
                                    backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                },
                                '.hierarchyItem:not(.active) &:hover': {
                                    color: 'text.primary',
                                },
                            }}
                            onClick={toggleOpen}
                        >
                            <IconMi
                                icon={isOpen ? 'nav-arrows-expand' : 'nav-arrows-collapse'}
                                sx={{ fontSize: '10px', opacity: !isOpen && !elementIsActive ? 0.24 : 1 }}
                            />
                        </Box>
                    ) : null}
                </Box>
                <NodeNameLine componentUid={componentUid} node={node} />
            </Stack>
            {isComponent && isOpen && (
                <NodeCssElements node={node} componentUid={componentUid} uid={node.params?.uid} />
            )}
        </Box>
    );
}
